import { Button, Icon, Input, message } from "antd";
import { RiChatHistoryLine } from "react-icons/ri";
import React, { useState } from "react";
import { useSender } from "../../Utils/useSender";
import { useUpdateMessage } from "../../Utils/useUpdateMessage";
import {
    errorMessage,
    CourseWareTypeList,
    uploadToQiNiu,
    useModalHandler
} from "../../polyfills";
import RecordListModal from "../RecordListModal";
import { ChatMessageItem } from "../../types";
import useMessageScroll from "../../Utils/useMessageScroll";
import { makeFileMessageContent, sleep } from "../../Utils/common";
import useUpdateSessionList from "../../Utils/useUpdateSessionList";
import { UploadManager } from "../../Utils/uploadManager";
import FileSelect from "./Components/FileSelect";
import style from "./index.module.scss";
import { checkCourseWare } from "./utils";
import CourseSelect from "./Components/CourseSelect";
import { useLoadCourse } from "./utils/useLoadCourse";

const { TextArea } = Input;

const uploadManager = new UploadManager({
    uploader: (file, onProgress) => {
        return uploadToQiNiu(file, {
            onUploadProgress: (file, percent) => onProgress(Math.floor(percent))
        }).then(data => data.url);
    }
});

interface SendMsgProps {
    sessionId: string;
    courseNo?: string; //课程号
    courseSeq?: string; //课程序号
}

export default function SendMsg({
    sessionId,
    courseNo,
    courseSeq
}: SendMsgProps) {
    const recordsModal = useModalHandler();
    const updateSessionList = useUpdateSessionList();

    //当前文本输入值
    const [text, setText] = useState<string>("");

    const sender = useSender();
    const { addMessage, updateMessage } = useUpdateMessage();
    const scroller = useMessageScroll();

    const { loading, data: courseList } = useLoadCourse({
        courseNo,
        courseSeq
    });

    //发消息
    const handleSendMsg = async () => {
        if (!text.trim()) return;

        const newMsg = sender.sendTextMessage(text);

        if (newMsg) {
            setText("");
            onSendSuccess(newMsg);
        }
    };

    function onPressEnter(e: any) {
        // 按住ctrl或者shift认为是正常回车
        if (e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        handleSendMsg();
    }

    // 发文件
    const handleSendFile = async (files: File[]) => {
        try {
            // 所有文件都先检查一遍, 全都没有问题再开始上传
            files.forEach(file => checkCourseWare(file, CourseWareTypeList));

            files.forEach(file => {
                const { messageInfo, sendToServer } =
                    sender.sendFileMessage(file);

                uploadManager
                    .upload(file, percent =>
                        updateMessage(sessionId, messageInfo.guid, {
                            ...messageInfo,
                            percent
                        })
                    )
                    .then(url => {
                        updateMessage(sessionId, messageInfo.guid, {
                            content: makeFileMessageContent(file, url),
                            error: undefined
                        });

                        sendToServer(url);
                    })
                    .catch(() => {
                        updateMessage(sessionId, messageInfo.guid, {
                            error: "文件上传失败"
                        });
                    })
                    .finally(() => {
                        updateMessage(sessionId, messageInfo.guid, {
                            uploading: false
                        });
                    });

                if (messageInfo) onSendSuccess(messageInfo);
            });
        } catch (e) {
            message.error(errorMessage(e));
        }
    };

    async function onSendSuccess(newMsg: ChatMessageItem) {
        addMessage(sessionId, newMsg);
        await sleep(0);
        scroller.scrollToBottom();
        updateSessionList();
    }

    return (
        <div className={`${style.sendMsg} WebIM-SendMsg`}>
            <div className={style.tools}>
                <FileSelect
                    disabled={courseList?.length === 0}
                    onFileSelect={handleSendFile}>
                    <Icon
                        type="folder"
                        className={style.icon}
                        title="上传附件"
                    />
                </FileSelect>

                <RiChatHistoryLine
                    className={style.icon}
                    title="聊天记录"
                    onClick={recordsModal.open}
                />

                <CourseSelect
                    allowSelect={!courseNo}
                    courseList={courseList || []}
                    loading={loading}
                />
            </div>

            <TextArea
                value={text}
                rows={3}
                allowClear
                className={style.textArea}
                maxLength={1000}
                placeholder={
                    courseList?.length === 0
                        ? "只有课程的授课老师和学生才能聊天"
                        : "请输入..."
                }
                onChange={e => setText(e.target.value)}
                onPressEnter={onPressEnter}
                disabled={courseList?.length === 0}
                // @ts-ignore
                enterkeyhint="send"
            />

            <Button
                type="link"
                className={style.sendBtn}
                onClick={handleSendMsg}
                disabled={courseList?.length === 0}>
                发送
            </Button>

            {recordsModal.render(
                <RecordListModal
                    onCancel={recordsModal.close}
                    sessionUid={sessionId}
                />
            )}
        </div>
    );
}
